<template>
  <div class="container-fluid catalog-page">
    <b-overlay :show="loading">
      <header class="catalog-hero">
        <div class="catalog-hero__copy">
          <div class="catalog-hero__eyebrow">
            <router-link :to="getLink('')">INICIO</router-link>
            <span aria-hidden="true">/</span>
            <span>{{ storeEyebrow }}</span>
          </div>
          <h1>{{ storeTitle }}</h1>
          <p>{{ storeSubtitle }}</p>
        </div>
        <img
          v-if="heroProductImage"
          class="catalog-hero__image"
          :src="getImage(heroProductImage, 'medium')"
          alt=""
          aria-hidden="true"
        />
        <div class="catalog-hero__glow" aria-hidden="true"></div>
      </header>

      <div class="row catalog-layout">
        <div v-if="showCategoryMenu" class="col-12 col-md-3 mb-4 catalog-sidebar-column">
          <aside class="catalog-sidebar">
            <div class="catalog-sidebar__title">
              <small>EXPLORAR</small>
              <h2><b-icon icon="grid-fill" aria-hidden="true" /> Categorías</h2>
            </div>
            <nav class="catalog-categories" aria-label="Categorías de productos">
              <div v-if="loadingCategories" class="catalog-category-skeleton">
                <b-skeleton v-for="p in 8" :key="p" class="my-3" width="90%" />
              </div>
              <button
                type="button"
                class="catalog-category"
                :class="{ 'catalog-category--active': $route.params.cname == null }"
                :aria-current="$route.params.cname == null ? 'page' : null"
                @click="clearCategory()"
              >
                <b-icon icon="grid-fill" aria-hidden="true" />
                <span>Todas las categorías</span>
              </button>
              <router-link
                v-for="cat in categories"
                :key="cat._id"
                v-show="cat._id != null"
                class="catalog-category"
                :class="{ 'catalog-category--active': $route.params.cid == cat._id }"
                :aria-current="$route.params.cid == cat._id ? 'page' : null"
                :to="getLink(`productos/categoria/${StringToSlugUrl(cat.name)}/${cat._id}`)"
              >
                <b-icon :icon="categoryMenuIcon(cat)" aria-hidden="true" />
                <span>{{ cat.name }}</span>
              </router-link>
            </nav>
            <router-link class="catalog-order-card" :to="getLink('pedido')">
              <span class="catalog-order-card__icon"><b-icon icon="bag" aria-hidden="true" /></span>
              <span class="catalog-order-card__copy">
                <strong>Ordena fácil<br />y rápido</strong>
                <small>Arma tu pedido con tus favoritos.</small>
              </span>
              <b-icon class="catalog-order-card__arrow" icon="arrow-right-circle" aria-hidden="true" />
            </router-link>
          </aside>
        </div>
        <main :class="showCategoryMenu ? 'col-12 col-md-9 catalog-results' : 'col-12 catalog-results'">
          <div class="catalog-toolbar">
            <div class="catalog-toolbar__title">
              <small class="catalog-eyebrow">EL MENÚ</small>
              <h2>{{ selectedCategoryName }}</h2>
              <small class="catalog-count">{{ loading ? 'Cargando productos...' : `${visibleProducts.length} productos disponibles` }}</small>
            </div>
            <div class="catalog-sort">
              <label for="catalog-order">ORDENAR POR</label>
              <select id="catalog-order" class="form-control" v-model="order">
                <option value="name">Por nombre (ascendente)</option>
                <option value="name_desc">Por nombre (descendente)</option>
                <option value="value">Por precio (ascendente)</option>
                <option value="value_desc">Por precio (descendente)</option>
              </select>
            </div>
          </div>
          <label v-if="showSearch" class="catalog-search">
            <b-icon icon="search" aria-hidden="true" />
            <span class="sr-only">Buscar productos</span>
            <input v-model.trim="searchQuery" type="search" placeholder="Buscar productos por nombre o descripción" autocomplete="off" />
          </label>
          <div class="catalog-products">
            <div class="catalog-product-skeleton" v-if="loading">
              <div class="catalog-product-skeleton__item" v-for="p in 6" :key="p">
                <b-skeleton-img />
                <b-skeleton class="mt-3" />
                <b-skeleton class="mt-2" width="65%" />
              </div>
            </div>
            <div class="catalog-empty" v-else-if="visibleProducts.length == 0">
              <div class="catalog-empty__icon"><b-icon icon="basket3" aria-hidden="true" /></div>
              <h3>{{ searchQuery ? 'No encontramos productos' : 'No hay productos en esta categoría' }}</h3>
              <p>{{ searchQuery ? 'Prueba con otro nombre o descripción.' : 'Prueba con otra categoría para seguir explorando.' }}</p>
              <button v-if="searchQuery" type="button" class="catalog-empty__button" @click="searchQuery = ''">Limpiar búsqueda</button>
              <button v-else type="button" class="catalog-empty__button" @click="clearCategory()">Ver todos los productos</button>
            </div>
            <div
              class="catalog-grid-item"
              v-else
              v-for="p in visibleProducts"
              :key="p._id"
              :class="productCardLayout === 'horizontal' ? 'catalog-grid-item--horizontal' : ''"
            >
              <single-product :data="p" variant="catalog" />
            </div>
          </div>
        </main>
      </div>
    </b-overlay>
    <b-modal
      modal-class="product-modal"
      content-class="product-modal__content"
      header-class="product-modal__header"
      body-class="product-modal__body"
      ref="modal"
      title-class="text-uppercase"
      size="lg"
      scrollable
      hide-footer
    >
      <product :id="pid" @success="closeModal()" :textColor="getThemeText()" />
    </b-modal>
  </div>
</template>

<script>
//import product from "./Product.vue";
const product = () => import("./Product.vue");
//import singleProduct from "../components/singleProduct";
const singleProduct = () => import("../components/singleProduct.vue");
export default {
  components: {
    product,
    singleProduct,
  },
  props: {
    showSearch: { type: Boolean, default: false },
    showCategoryMenu: {
      type: Boolean,
      default: true,
    },
    storeEyebrow: {
      type: String,
      default: "NUESTRA TIENDA",
    },
    storeTitle: {
      type: String,
      default: "Explora el menú",
    },
    storeSubtitle: {
      type: String,
      default: "Elige tus favoritos, a tu ritmo.",
    },
  },
  data() {
    return {
      loading: true,
      loadingProduct: true,
      loadingCategories: true,
      categories: [],
      products: [],
      searchQuery: "",
      category: {},
      productsTotal: 0,
      pid: 0,
      order: ["name", "name_desc", "value", "value_desc"].includes(this.$route.query.order)
        ? this.$route.query.order
        : "name",
    };
  },
  mounted() {
    window.scrollTo(0, 0);
    this.loadCategories();
    this.loadProducts();
  },
  methods: {
    clearCategory() {
      // remove cid and cname from url
      this.$router.push(this.getLink("productos"));
    },
    viewProduct(pid, pname = "") {
      //const { info } = this.$store.state;
      //this.$router.push(`/${info.webName}/p/${pid}`);
      this.pid = pid;
      if (pname) this.productName = pname;
      this.$refs.modal.show();
      try {
        this.$gtag.event("product_view", {
          event_category: "product",
          event_label: "User view product",
          value: 1,
        });
        if (this.settings.facebookPixelEnabled && this.$analytics) {
          console.log("track view content");
          this.safeFbq("track", "ViewContent", {
            pid: this.pid,
            content_name: pname,
          });
        }
      } catch (error) {
        console.log("error stats");
      }
    },
    StringToSlugUrl(str) {
      // replace all ñ to n
      return str
        .toLowerCase()
        .replace(/\//g, "")
        .replace(/ /g, "-")
        .replace(/ñ/g, "n");
    },
    categoryMenuIcon(category) {
      const configured = category.webIcon || category.menu_icon || category.icon;
      if (typeof configured === "string" && configured.trim()) return configured;

      const name = String(category.name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      if (/promo|oferta|descuento/.test(name)) return "tag-fill";
      if (/bebida|cafe|milkshake|jugo/.test(name)) return "cup-straw";
      if (/burger|sandwich|panini/.test(name)) return "egg-fried";
      if (/sushi|roll|handroll/.test(name)) return "circle";
      if (/ceviche|gohan|bowl/.test(name)) return "basket";
      if (/cobertura|extra/.test(name)) return "layers";
      if (/variedad/.test(name)) return "grid-3x3-gap";
      return "bag";
    },
    filterByCategory(cat) {
      this.loading = true;
      this.category = cat;
    },
    loadCategories() {
       let { webName } = this.$store.state.info;
      if(webName == undefined) return false

      this.$http
        .get(`${webName}/category`)
        .then((res) => {
          // sort by name
          res.data.data.sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          });
          this.categories = res.data.data;
        })
        .finally(() => {
          this.loadingCategories = false;
        });
    },
    loadProducts() {
      // this.loading = true;
      let { webName } = this.$store.state.info;
      let { cid } = this.$route.params;
      let query = {
        order: this.order,
      };
      if (cid) query.category = cid;

      const u = new URLSearchParams(query).toString();
      
      this.$http
        .get(`${webName}/product?${u}`)
        .then((res) => {
          this.products = res.data.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadPage() {
      let {webName} = this.$store.state.info;

      this.$http
        .get(`${webName}/page/_productfooter`)
        .then((res) => {
          console.log('settings components')
          this.$store.commit("setComponents", res.data.data.components)
        })
    },
  },
  computed: {
    visibleProducts() {
      if (!this.showSearch || !this.searchQuery) return this.products;
      const query = this.searchQuery.toLocaleLowerCase();
      return this.products.filter((product) =>
        `${product.name || ''} ${product.description || ''}`.toLocaleLowerCase().includes(query)
      );
    },
    productCardLayout() {
      const settings = this.$store.state.info && this.$store.state.info.settings;
      const configuredLayout = settings && settings.productCardLayoutConfigured === true
        ? settings.productCardLayout
        : null;
      if (configuredLayout === 'vertical' || configuredLayout === 'horizontal') {
        return configuredLayout;
      }

      const legacyLayout = settings && settings.singleProductLayout;
      if (legacyLayout === 'vertical' || legacyLayout === 'horizontal') {
        return legacyLayout;
      }

      return 'horizontal';
    },
    selectedCategoryName() {
      const selected = this.categories.find((cat) => cat._id == this.$route.params.cid);
      return selected ? selected.name : "Todos los productos";
    },
    heroProductImage() {
      const productWithImage = this.products.find((product) => product && product.image);
      return productWithImage ? productWithImage.image : "";
    },
    info() {
      return this.$store.state.info;
    },
  },
  watch: {
    order() {
      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          order: this.order,
        },
      });
    },
    info() {
      this.loadProducts();
      this.loadCategories();
      this.loadPage()
    },
    $route() {
      //this.loading = true;
      this.loadProducts();
    },
  },
};
</script>

<style scoped>
.catalog-page {
  --catalog-ink: #152a4b;
  --catalog-muted: #687b98;
  --catalog-accent: #1478ed;
  --catalog-warm: #b94f28;
  width: 100%;
  max-width: 1480px;
  margin-right: auto;
  margin-left: auto;
  min-height: 70vh;
  padding: 22px clamp(14px, 2.5vw, 36px) 72px;
  color: var(--catalog-ink);
  background:
    radial-gradient(ellipse at 87% 4%, rgba(255, 222, 165, 0.36), transparent 30%),
    #fbfaf7;
}

.catalog-search {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  padding: 10px 15px;
  border: 1px solid #dbe5f1;
  border-radius: 12px;
  background: #fff;
  color: var(--catalog-muted);
}

.catalog-search:focus-within { border-color: var(--catalog-accent); box-shadow: 0 0 0 3px rgba(20, 120, 237, 0.1); }
.catalog-search input { width: 100%; border: 0; outline: 0; background: transparent; color: var(--catalog-ink); }
.catalog-search input::placeholder { color: #8a9bb0; }

.catalog-hero {
  position: relative;
  display: flex;
  min-height: clamp(154px, 18vw, 220px);
  align-items: center;
  overflow: hidden;
  margin: 0 -8px 20px;
  padding: 24px clamp(20px, 4vw, 48px);
  border: 1px solid rgba(230, 221, 206, 0.68);
  border-radius: 22px;
  background: linear-gradient(105deg, #fffefa 0%, #fffaf0 55%, #f3e5d0 100%);
  box-shadow: 0 12px 36px rgba(25, 43, 70, 0.045);
  isolation: isolate;
  animation: catalog-enter 420ms ease-out both;
}

.catalog-hero__copy {
  position: relative;
  z-index: 2;
  max-width: min(64%, 720px);
}

.catalog-hero__eyebrow {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 7px;
  color: #a44c2e;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.catalog-hero__eyebrow a {
  color: inherit;
}

.catalog-hero h1,
.catalog-toolbar h2,
.catalog-sidebar h2 {
  margin: 0;
  color: var(--catalog-ink);
  font-family: inherit;
  font-weight: 850;
  letter-spacing: -0.045em;
}

.catalog-hero h1 {
  font-size: clamp(2.1rem, 4.6vw, 3.8rem);
  line-height: 1.02;
}

.catalog-hero p {
  margin: 8px 0 0;
  color: var(--catalog-muted);
  font-size: clamp(0.88rem, 1.3vw, 1.04rem);
}

.catalog-hero__image {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  width: 48%;
  height: 100%;
  object-fit: cover;
  object-position: center 48%;
  opacity: 0.96;
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.56) 22%, #000 48%);
  mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.56) 22%, #000 48%);
}

.catalog-hero__glow {
  position: absolute;
  z-index: 1;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(90deg, rgba(255, 254, 250, 0) 48%, rgba(255, 248, 235, 0.14) 100%);
}

.catalog-sidebar {
  position: sticky;
  top: 16px;
  padding: 17px 10px 10px;
  border: 1px solid #edf0f5;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0 12px 36px rgba(26, 46, 73, 0.065);
}

.catalog-sidebar__title {
  padding: 0 12px 12px;
}

.catalog-sidebar__title small {
  display: block;
  margin-bottom: 5px;
  color: #8291a7;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.catalog-sidebar h2 {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.32rem;
  letter-spacing: -0.035em;
}

.catalog-sidebar h2 .b-icon {
  color: var(--catalog-accent);
  font-size: 1rem;
}

.catalog-categories {
  display: flex;
  flex-direction: column;
}

.catalog-category-skeleton {
  padding: 0 12px 10px;
}

.catalog-category {
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  min-height: 42px;
  padding: 8px 12px;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  border-radius: 0;
  background: transparent;
  color: #344760;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 650;
  text-align: left;
  text-decoration: none;
  transition: background-color 160ms ease, color 160ms ease, border-radius 160ms ease;
}

.catalog-category:last-child {
  border-bottom-color: transparent;
}

.catalog-category .b-icon {
  flex: 0 0 18px;
  color: #74869a;
  font-size: 1rem;
  text-align: center;
}

.catalog-category:hover,
.catalog-category:focus-visible {
  border-radius: 9px;
  background: #f1f6fd;
  color: var(--catalog-accent);
  text-decoration: none;
}

.catalog-category:focus-visible {
  outline: 2px solid rgba(20, 120, 237, 0.55);
  outline-offset: -2px;
}

.catalog-category--active {
  border-color: transparent;
  border-radius: 9px;
  background: #eaf3ff;
  color: #0868d4;
  font-weight: 800;
}

.catalog-category--active .b-icon {
  color: var(--catalog-accent);
}

.catalog-order-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
  margin-top: 12px;
  padding: 15px 12px;
  border-radius: 14px;
  background: linear-gradient(120deg, #fff0d8, #fff7e8);
  color: var(--catalog-ink);
  text-decoration: none;
}

.catalog-order-card::after {
  position: absolute;
  top: -32px;
  right: -18px;
  width: 94px;
  height: 94px;
  border-radius: 50%;
  background: rgba(235, 160, 73, 0.11);
  content: "";
}

.catalog-order-card:hover,
.catalog-order-card:focus-visible {
  color: var(--catalog-ink);
  text-decoration: none;
  box-shadow: inset 0 0 0 1px rgba(185, 79, 40, 0.16);
}

.catalog-order-card__icon {
  z-index: 1;
  color: var(--catalog-warm);
  font-size: 1.65rem;
}

.catalog-order-card__copy {
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
}

.catalog-order-card__copy strong {
  font-size: 0.88rem;
  line-height: 1.2;
}

.catalog-order-card__copy small {
  margin-top: 4px;
  color: var(--catalog-muted);
  font-size: 0.69rem;
  line-height: 1.35;
}

.catalog-order-card__arrow {
  z-index: 1;
  color: #cb783c;
  font-size: 1.25rem;
}

.catalog-results {
  min-width: 0;
  padding: 0 0 12px;
}

.catalog-toolbar {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 16px;
  padding: 16px 18px;
  border: 1px solid #f0eee8;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.84);
  box-shadow: 0 6px 22px rgba(32, 49, 71, 0.04);
}

.catalog-toolbar .catalog-eyebrow {
  margin: 0 0 4px;
  color: #b44e2c;
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.17em;
}

.catalog-toolbar h2 {
  font-size: clamp(1.5rem, 2.5vw, 2.25rem);
  line-height: 1.1;
}

.catalog-count {
  color: var(--catalog-muted);
  display: inline-block;
  margin-top: 5px;
  font-size: 0.78rem;
}

.catalog-sort {
  flex: 0 1 230px;
}

.catalog-sort label {
  display: block;
  margin-bottom: 5px;
  color: #8291a7;
  font-size: 0.63rem;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.catalog-sort select {
  height: 40px;
  border: 1px solid #e0e5ec;
  border-radius: 10px;
  background-color: #fff;
  color: var(--catalog-ink);
  font-size: 0.78rem;
  box-shadow: 0 3px 8px rgba(26, 46, 73, 0.025);
}

.catalog-products {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 15px;
}

.catalog-grid-item {
  min-width: 0;
  animation: catalog-enter 350ms ease-out both;
}

.catalog-empty {
  grid-column: 1 / -1;
  padding: 72px 18px;
  color: var(--catalog-muted);
  text-align: center;
}

.catalog-empty__icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  margin: 0 auto 12px;
  border-radius: 17px;
  background: #eaf3ff;
  color: var(--catalog-accent);
  font-size: 1.5rem;
}

.catalog-empty h3 {
  margin: 0;
  color: var(--catalog-ink);
  font-size: 1.16rem;
  font-weight: 800;
}

.catalog-empty p {
  margin: 7px 0 18px;
}

.catalog-empty__button {
  padding: 9px 16px;
  border: 0;
  border-radius: 999px;
  background: var(--catalog-accent);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 750;
}

.catalog-product-skeleton {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;
}

.catalog-product-skeleton__item {
  min-width: 0;
  overflow: hidden;
  padding: 10px 10px 16px;
  border: 1px solid #f0eee8;
  border-radius: 16px;
  background: #fff;
}

@keyframes catalog-enter {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 767.98px) {
  .catalog-sidebar {
    position: static;
    padding-bottom: 12px;
  }

  .catalog-categories {
    display: flex;
    flex-wrap: nowrap;
    gap: 7px;
    overflow-x: auto;
    padding: 0 3px 5px;
    scrollbar-width: none;
  }

  .catalog-categories::-webkit-scrollbar {
    display: none;
  }

  .catalog-category,
  .catalog-category:last-child {
    flex: 0 0 auto;
    width: auto;
    max-width: 230px;
    min-height: 38px;
    border: 1px solid #e5eaf1;
    border-radius: 999px;
    padding: 7px 13px;
    background: #fff;
    font-size: 0.74rem;
  }

  .catalog-category .b-icon {
    flex-basis: auto;
  }

  .catalog-category--active {
    border-color: rgba(20, 120, 237, 0.35);
  }

  .catalog-order-card { display: none; }
}

@media (max-width: 575.98px) {
  .catalog-page {
    padding: 14px 12px 46px;
  }

  .catalog-hero {
    min-height: 148px;
    margin: 0 0 14px;
    padding: 22px 18px;
    border-radius: 17px;
  }

  .catalog-hero__copy {
    max-width: 78%;
  }

  .catalog-hero h1 {
    font-size: clamp(1.8rem, 9vw, 2.55rem);
  }

  .catalog-hero__image {
    right: -18%;
    width: 62%;
    opacity: 0.76;
  }

  .catalog-hero__eyebrow {
    font-size: 0.57rem;
    letter-spacing: 0.12em;
  }

  .catalog-hero p {
    max-width: 240px;
    font-size: 0.82rem;
  }

  .catalog-sidebar {
    padding: 13px 10px 10px;
    border-radius: 15px;
  }

  .catalog-sidebar__title {
    padding-bottom: 9px;
  }

  .catalog-results {
    padding: 0;
  }

  .catalog-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 13px;
    margin-bottom: 12px;
    padding: 15px;
    border-radius: 14px;
  }

  .catalog-sort {
    flex-basis: auto;
    width: 100%;
  }

  .catalog-products,
  .catalog-product-skeleton {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }

  .catalog-empty {
    padding: 48px 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .catalog-hero,
  .catalog-grid-item {
    animation: none;
  }

  .catalog-category {
    transition: none;
  }
}
</style>
